import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import {
  BackToChat,
  IconBrokenLink,
  IconRefresh,
  IconStruckCircle,
  IconWifiOff,
  Wordmark,
} from '@/components/chrome';
import { LiveTicketQr } from '@/components/dynamic-qr';
import { ChatApiError, fetchTicketView } from '@/lib/http';
import type { TicketView } from '@/lib/types';

export const Route = createFileRoute('/t/$token')({
  component: TicketPage,
});

// Re-poll cadence: the QR itself rotates purely client-side, so this only exists
// to catch a status flip (claimed / cancelled → blocked). A failed re-poll never
// disturbs a working ticket (EC-WEB-2) — the in-memory payload keeps rotating.
const REPOLL_MS = 30_000;

function TicketPage() {
  const { token } = Route.useParams();
  const [view, setView] = useState<TicketView | null>(null);
  const [offsetMs, setOffsetMs] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  // Whether we have ever loaded a payload — a later fetch failure must not wipe it.
  const hasView = useRef(false);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const v = await fetchTicketView(token, signal);
        setView(v);
        // Anchor the QR clock to the server's: offset = serverClock − clientClock.
        setOffsetMs(v.serverTimeMs - Date.now());
        setError(null);
        hasView.current = true;
      } catch (e) {
        if (signal?.aborted) return;
        // Only surface an error when there is nothing to show; a re-poll failure
        // on an already-rendered ticket is swallowed so the QR keeps working.
        if (!hasView.current) {
          setError(e instanceof Error ? e : new Error('failed to load ticket'));
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    const id = window.setInterval(() => void load(), REPOLL_MS);
    return () => {
      ctrl.abort();
      window.clearInterval(id);
    };
  }, [load]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    void load();
  }, [load]);

  if (!view) {
    if (loading) return <TicketLoading />;
    return <TicketError error={error} onRetry={retry} />;
  }
  return <TicketBody view={view} offsetMs={offsetMs} />;
}

/* ---- B2 / B2·multi / B3 / B6 ------------------------------------------ */
function TicketBody({ view, offsetMs }: { view: TicketView; offsetMs: number }) {
  const online = useOnline();

  if (view.blocked) return <BlockedTicket view={view} />;

  return (
    <main className="app">
      <div className="topbar">
        <Journey from={view.from} to={view.to} />
      </div>
      {!online && (
        <div style={{ padding: '14px 0 2px' }}>
          <div className="offline">
            <IconWifiOff className="dg" />
            Offline · your ticket still works
          </div>
        </div>
      )}
      {view.passes.length > 1 ? (
        <MultiPass view={view} offsetMs={offsetMs} online={online} />
      ) : (
        <SinglePass view={view} offsetMs={offsetMs} online={online} />
      )}
    </main>
  );
}

function SinglePass({
  view,
  offsetMs,
  online,
}: {
  view: TicketView;
  offsetMs: number;
  online: boolean;
}) {
  const pass = view.passes[0];
  return (
    <>
      <div style={contentRegion}>
        {pass && (
          <LiveTicketQr
            staticBlock={pass.qrStaticBlock}
            lat={pass.qrLat}
            lng={pass.qrLng}
            refreshSeconds={pass.refreshSeconds}
            offsetMs={offsetMs}
            hint={online ? undefined : 'Rotating from your phone — no signal needed'}
          />
        )}
        <div className="facts">
          <Fact label="Passengers" value={passengerLabel(view.passengerCount)} />
          <Fact label="Valid until" value={pass ? formatValidUntil(pass.validUntil) : '—'} />
        </div>
      </div>
      <Footnote>Hold this up to the gate scanner</Footnote>
    </>
  );
}

function MultiPass({
  view,
  offsetMs,
  online,
}: {
  view: TicketView;
  offsetMs: number;
  online: boolean;
}) {
  const total = view.passes.length;
  const [active, setActive] = useState(0);
  const touchX = useRef<number | null>(null);

  const go = (i: number) => setActive(Math.max(0, Math.min(total - 1, i)));
  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    if (dx < -40) go(active + 1);
    else if (dx > 40) go(active - 1);
    touchX.current = null;
  };

  const pass = view.passes[active];
  return (
    <div style={{ ...contentRegion, gap: 20 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          padding: '0 2px',
        }}
      >
        <span className="serif" style={{ fontSize: 22 }}>
          Pass <span className="serif-i">{ordinalWord(active + 1)}</span> of {numberWord(total)}
        </span>
        <span className="pill t-accent2">Scan each once</span>
      </div>
      <div className="stack" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {total > 2 && <span className="peek b" />}
        <span className="peek a" />
        <div style={{ position: 'relative', zIndex: 1, background: 'var(--bg)' }}>
          {pass && (
            <LiveTicketQr
              key={active}
              staticBlock={pass.qrStaticBlock}
              lat={pass.qrLat}
              lng={pass.qrLng}
              refreshSeconds={pass.refreshSeconds}
              offsetMs={offsetMs}
              liveLabel="Live"
              hint={
                online
                  ? `Rider ${active + 1} · valid until ${formatTime(pass.validUntil)}`
                  : 'Rotating from your phone — no signal needed'
              }
            />
          )}
        </div>
      </div>
      <div className="pager">
        {view.passes.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`p ${i === active ? 'on' : ''}`}
            aria-label={`Show pass ${i + 1} of ${total}`}
            aria-current={i === active}
            onClick={() => go(i)}
          />
        ))}
      </div>
      <Footnote>Swipe for the next rider's code</Footnote>
    </div>
  );
}

/* ---- B3 Blocked (terminal — no scannable QR) -------------------------- */
function BlockedTicket({ view }: { view: TicketView }) {
  const { label, reason, body } = terminalCopy(view);
  return (
    <main className="app">
      <div className="topbar">
        <Journey from={view.from} to={view.to} dim />
      </div>
      <div style={contentRegion}>
        <div className="qrblock">
          <div className="live">
            <span className="pill t-danger boxed">
              <span className="dot" />
              Not valid
            </span>
            <span className="mono muted" style={{ fontSize: 11 }}>
              {reason}
            </span>
          </div>
          <div className="crop void">
            <span className="ck" />
            <span className="ck" />
            <span className="ck" />
            <span className="ck" />
            <div className="void-body">
              <IconStruckCircle className="glyph" />
              <div className="lbl">{label}</div>
            </div>
          </div>
        </div>
        <p className="sub" style={{ textAlign: 'center', maxWidth: 270, margin: '0 auto' }}>
          {body}
        </p>
        <Footnote>Book a new ticket in your chat to travel again</Footnote>
      </div>
    </main>
  );
}

/* ---- B1 Loading -------------------------------------------------------- */
function TicketLoading() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
        <span className="pill t-muted">
          <span className="dot pulse" />
          Loading
        </span>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 24,
        }}
      >
        <div className="sk" style={{ width: '100%', height: 16 }} />
        <div
          className="sk"
          style={{ width: '74%', aspectRatio: '1', borderRadius: 6, alignSelf: 'center' }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="sk" style={{ width: '100%', height: 14 }} />
          <div className="sk" style={{ width: '100%', height: 14 }} />
        </div>
      </div>
      <p className="sub muted" style={{ textAlign: 'center', fontSize: 13 }}>
        Loading your ticket…
      </p>
    </main>
  );
}

/* ---- B4 Invalid link / B5 Unavailable --------------------------------- */
function TicketError({ error, onRetry }: { error: Error | null; onRetry: () => void }) {
  const status = error instanceof ChatApiError ? error.status : 0;
  const invalid = status === 401 || status === 400;
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
      </div>
      {invalid ? (
        <div className="state">
          <IconBrokenLink className="glyph warn" />
          <h3>
            This ticket link
            <br />
            is invalid
          </h3>
          <p className="sub">
            The link may have expired or been mistyped. Open the most recent ticket link WeeBee sent
            in your chat.
          </p>
        </div>
      ) : (
        <div className="state">
          <IconRefresh className="glyph" />
          <h3>
            Temporarily
            <br />
            unavailable
          </h3>
          <p className="sub">
            We couldn't reach the ticket service. Check your connection and try again in a moment.
          </p>
          <button
            type="button"
            className="btn btn--ink"
            style={{ maxWidth: 210 }}
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      )}
      {invalid ? <BackToChat /> : <Footnote>Your ticket is safe — this is just a hiccup</Footnote>}
    </main>
  );
}

/* ---- shared bits ------------------------------------------------------- */
function Journey({ from, to, dim }: { from: string; to: string; dim?: boolean }) {
  return (
    <div className={`journey${dim ? ' dim' : ''}`} style={{ width: '100%' }}>
      <span className="pt">
        <i className="dot o" />
        <span className="nm">{from || '—'}</span>
      </span>
      <span className="line" />
      <span className="pt end">
        <span className="nm">{to || '—'}</span>
        <i className="dot d" />
      </span>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="frow">
      <span className="l">{label}</span>
      <span className="dts" />
      <span className="v">{value}</span>
    </div>
  );
}

function Footnote({ children }: { children: ReactNode }) {
  return (
    <div className="footnote">
      <span className="ln" />
      <span className="tx">{children}</span>
      <span className="ln" />
    </div>
  );
}

const contentRegion = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center' as const,
  gap: 22,
};

// navigator.onLine + online/offline events — used only to surface the offline
// hint; the QR keeps rotating from memory regardless (EC-WEB-2).
function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function passengerLabel(n: number): string {
  return `${n} adult${n === 1 ? '' : 's'}`;
}

// Terminal-state copy: never a scannable code, always icon + words (accessibility).
function terminalCopy(view: TicketView): { label: string; reason: string; body: string } {
  if (view.status === 'COMPLETED') {
    return {
      label: 'Journey complete',
      reason: 'completed',
      body: 'This journey is finished. Book a new ticket in your chat to travel again.',
    };
  }
  if (view.status === 'CANCELLED') {
    return {
      label: 'Ticket cancelled',
      reason: 'cancelled',
      body: 'This ticket was cancelled — there is no code to show. Book a new ticket in your chat.',
    };
  }
  const claimed = view.passes.some((p) => p.ticketStatus === 'CLAIMED');
  if (claimed) {
    return {
      label: 'Ticket used',
      reason: 'used',
      body: "This pass was already scanned at the gate. A ticket can only be used once — there's no code to show.",
    };
  }
  return {
    label: 'Ticket expired',
    reason: 'expired',
    body: "This ticket's validity has passed — there's no code to show. Book a new ticket in your chat.",
  };
}

// RFC3339 → "2:30 AM · 20 May"; falls back to the raw string if unparseable.
function formatValidUntil(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${formatTime(iso)} · ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
}

// RFC3339 → "2:30 AM".
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];
function numberWord(n: number): string {
  return WORDS[n] ?? String(n);
}
function ordinalWord(n: number): string {
  return WORDS[n] ?? String(n);
}
